import { Alert, Button, Modal, Select, Textarea, TextInput } from '@mantine/core';
import {
  IconFileSpreadsheet,
  IconFileText,
  IconFileZip,
  IconLock,
  IconLockOpen,
} from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import { XLSX_TYPE } from '../reports/xlsx.js';
import classes from './Ledger.module.css';
import { mt, Notices, useLedgerError } from './ledger-ui.js';
import { ZIP_TYPE } from './period-package.js';
import cx from './Reconciliation.module.css';
import type { FuelSource } from './source.js';
import type {
  FuelMonthStatus,
  FuelPeriodData,
  FuelPeriodImpact,
  FuelQualityKind,
  FuelReconciliationData,
} from './types.js';

/** Önceki UTC ay (`YYYY-MM`): kapanışa varsayılan aday. */
export function previousMonth(now = new Date()): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  return d.toISOString().slice(0, 7);
}

const utc = (at: string | null | undefined) =>
  at ? `${at.slice(8, 10)}.${at.slice(5, 7)}.${at.slice(0, 4)} ${at.slice(11, 16)}` : '';

const QUALITY_KINDS: FuelQualityKind[] = [
  'missing_data',
  'estimate',
  'unmatched',
  'late',
  'unapproved_adjustment',
];

/**
 * ROB ay kapanışı, dönem kilidi, kalite listesi ve dışa aktarım (ADR 0117 §10, Ek Y4, Ek Y7).
 * Ay gemide kapanır (onaylayıcı); kapanmış ayı ofis kilitler ve gerekçe + etki raporuyla yeniden
 * açar; gemide kilit salt okunurdur. Kalite listesi ve dışa aktarım (Excel, PDF, paket) iki
 * tarafta aynıdır; dışa aktarım `fuel.export` ister.
 */
export function PeriodPanel({
  source,
  vesselId,
  canClose,
  tankName,
  fuelName,
  closes,
  onClosed,
}: {
  source: FuelSource;
  vesselId: string | null;
  canClose: boolean;
  tankName: (id: string) => string;
  fuelName: (id: string) => string;
  closes: FuelReconciliationData['closes'];
  onClosed: () => void;
}) {
  const { t } = useTranslation();
  const errorText = useLedgerError();
  const [month, setMonth] = useState(previousMonth);
  const [status, setStatus] = useState<FuelMonthStatus | FuelPeriodData | null>(null);
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [modal, setModal] = useState<'lock' | 'reopen' | null>(null);

  // Hata metni her çizimde yeni fonksiyondur: yükleme ona bağlanmaz (sonsuz yeniden yükleme olmasın)
  const errorRef = useRef(errorText);
  errorRef.current = errorText;
  const load = useCallback(async () => {
    try {
      if (source.period) setStatus(await source.period(vesselId, month));
      else if (source.month) setStatus(await source.month(vesselId, month));
    } catch (failure) {
      setError(errorRef.current(failure));
    }
  }, [source, vesselId, month]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!source.month && !source.period) return null;
  const months = Array.from({ length: 12 }, (_, i) => {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1))
      .toISOString()
      .slice(0, 7);
  });
  const blockerText = (b: FuelMonthStatus['blockers'][number]) =>
    t(`fuel.rob.month.blockers.${b.code}`, {
      tank: b.tankId ? tankName(b.tankId) : '',
      at: b.at ? utc(b.at) : '',
      no: b.recordNo ?? '',
      defaultValue: b.code,
    });
  const closedBy = closes.find((c) => c.month === month);
  const period = status && 'lock' in status ? status : null;
  const lock = period?.lock ?? null;
  const locked = lock?.state === 'locked';
  const can = period?.can;

  const save = async (kind: 'excel' | 'pdf' | 'zip') => {
    if (!source.periodExport || !source.saveFile) return;
    setBusy(kind);
    setError(null);
    setDone(null);
    try {
      const data = await source.periodExport(vesselId, month);
      const exporter = await import('./period-export.js');
      const tx = (key: string, options?: Record<string, unknown>) =>
        String(t(key, (options ?? {}) as never));
      const base = exporter.periodFileBase(data);
      const generated = new Date().toISOString();
      if (kind === 'excel') {
        await source.saveFile(`${base}.xlsx`, await exporter.periodWorkbook(data, tx), XLSX_TYPE);
      } else if (kind === 'pdf') {
        await source.saveFile(
          `${base}.pdf`,
          await exporter.periodPdf(data, tx, generated),
          'application/pdf',
        );
      } else {
        const { zip, files } = await exporter.periodZip(data, tx, generated);
        await source.saveFile(`${base}.zip`, zip, ZIP_TYPE);
        setDone(t('fuel.period.export.done', { count: files }));
      }
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <section className={classes.card} aria-label={t('fuel.rob.month.title')}>
        <div className={classes.head}>
          <h2 className={classes.title}>{t('fuel.rob.month.title')}</h2>
          <Select
            className={cx.monthSelect}
            aria-label={t('fuel.rob.month.month')}
            data={months}
            value={month}
            allowDeselect={false}
            onChange={(v) => {
              if (v) {
                setMonth(v);
                setDone(null);
                setError(null);
              }
            }}
          />
        </div>
        <div className={classes.body}>
          <Notices error={error} done={done} />
          {status?.closed ? (
            <Alert
              color="green"
              role="status"
              icon={<IconLock size={18} />}
              className={classes.banner}
            >
              {t('fuel.rob.month.closed', {
                at: status.closed.closedAt.slice(0, 10),
                by: closedBy?.closedBy ?? '—',
              })}
            </Alert>
          ) : status && !status.ended ? (
            <p className={classes.hint}>{t('fuel.rob.month.notEnded')}</p>
          ) : status && status.blockers.length === 0 ? (
            <p className={classes.hint}>{t('fuel.rob.month.noBlockers')}</p>
          ) : null}
          {status && !status.closed && status.blockers.length > 0 ? (
            <ul className={cx.blockers}>
              {status.blockers.map((b) => (
                <li
                  key={`${b.code}-${b.tankId ?? b.recordId ?? b.reportId ?? b.soundingId ?? ''}-${b.at ?? ''}`}
                >
                  {blockerText(b)}
                </li>
              ))}
            </ul>
          ) : null}
          {canClose && status && !status.closed && status.ended && !locked ? (
            <div className={cx.closeRow}>
              <TextInput
                className={cx.closeRemarks}
                label={t('fuel.rob.month.remarks')}
                value={remarks}
                maxLength={1000}
                onChange={(e) => setRemarks(e.currentTarget.value)}
              />
              <Button
                leftSection={<IconLock size={16} />}
                loading={busy === 'close'}
                disabled={status.blockers.length > 0}
                onClick={async () => {
                  if (!source.soundingEditor) return;
                  setBusy('close');
                  setError(null);
                  try {
                    await source.soundingEditor.closeMonth(month, remarks.trim() || null);
                    setDone(t('fuel.rob.month.closedDone', { month }));
                    setRemarks('');
                    await load();
                    onClosed();
                  } catch (failure) {
                    setError(errorText(failure));
                  } finally {
                    setBusy(null);
                  }
                }}
              >
                {t('fuel.rob.month.close')}
              </Button>
            </div>
          ) : null}

          {lock ? (
            <div className={cx.periodLock}>
              <div className={cx.periodLockHead}>
                <h3 className={cx.periodLockTitle}>{t('fuel.period.lock.title')}</h3>
                <span
                  className={`${cx.lockBadge} ${locked ? cx.lockBadge_locked : ''}`}
                  data-testid="period-lock-state"
                >
                  {locked ? (
                    <IconLock size={14} aria-hidden />
                  ) : (
                    <IconLockOpen size={14} aria-hidden />
                  )}
                  {locked ? t('fuel.period.lock.locked') : t('fuel.period.lock.open')}
                </span>
                <div className={cx.periodLockActions}>
                  {can?.lock && !locked && status?.closed ? (
                    <Button
                      size="sm"
                      leftSection={<IconLock size={16} />}
                      onClick={() => setModal('lock')}
                    >
                      {t('fuel.period.lock.lockAction')}
                    </Button>
                  ) : null}
                  {can?.reopen && locked ? (
                    <Button
                      size="sm"
                      variant="default"
                      leftSection={<IconLockOpen size={16} />}
                      onClick={() => setModal('reopen')}
                    >
                      {t('fuel.period.lock.reopenAction')}
                    </Button>
                  ) : null}
                </div>
              </div>
              {locked && lock.current ? (
                <p className={classes.hint}>
                  {t('fuel.period.lock.lockedBy', {
                    at: utc(lock.current.at),
                    by: lock.current.by ?? '—',
                  })}
                </p>
              ) : can?.lock && !status?.closed ? (
                <p className={classes.hint}>{t('fuel.period.lock.notClosed')}</p>
              ) : null}
              {locked && lock.late.count > 0 ? (
                <Alert color="orange" role="status" className={classes.banner}>
                  {t('fuel.period.lock.late', { count: lock.late.count })}
                  {Object.keys(lock.difference).length > 0
                    ? ` ${t('fuel.period.lock.difference', {
                        diff: Object.entries(lock.difference)
                          .map(([fuel, value]) => `${fuelName(fuel)} ${mt(value, 3)} MT`)
                          .join(', '),
                      })}`
                    : ''}
                </Alert>
              ) : null}
              {lock.events.length > 0 ? (
                <div
                  className={classes.tableWrap}
                  // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
                  tabIndex={0}
                >
                  <table className={classes.table} aria-label={t('fuel.period.lock.history')}>
                    <thead>
                      <tr>
                        <th scope="col">{t('fuel.period.lock.seq')}</th>
                        <th scope="col">{t('fuel.period.lock.event')}</th>
                        <th scope="col">{t('fuel.period.lock.at')}</th>
                        <th scope="col">{t('fuel.period.lock.by')}</th>
                        <th scope="col">{t('fuel.period.lock.reason')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lock.events.map((event) => (
                        <tr key={event.id}>
                          <td className={classes.num}>{event.seq}</td>
                          <td>{t(`fuel.period.lock.events.${event.event}`)}</td>
                          <td>{utc(event.at)}</td>
                          <td>{event.by ?? '—'}</td>
                          <td className={cx.wrapCell}>{event.reason ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
              {source.scope === 'ship' ? (
                <p className={classes.hint}>{t('fuel.period.lock.shipNote')}</p>
              ) : null}
            </div>
          ) : (
            <p className={classes.hint}>{t('fuel.rob.month.lockNote')}</p>
          )}
        </div>
      </section>

      {period ? (
        <section className={classes.card} aria-label={t('fuel.period.quality.title')}>
          <div className={classes.head}>
            <h2 className={classes.title}>{t('fuel.period.quality.title')}</h2>
            {period.can.export && source.periodExport && source.saveFile ? (
              <div className={cx.exportActions}>
                <Button
                  size="sm"
                  variant="default"
                  leftSection={<IconFileSpreadsheet size={16} />}
                  loading={busy === 'excel'}
                  onClick={() => void save('excel')}
                >
                  {t('fuel.period.export.excel')}
                </Button>
                <Button
                  size="sm"
                  variant="default"
                  leftSection={<IconFileText size={16} />}
                  loading={busy === 'pdf'}
                  onClick={() => void save('pdf')}
                >
                  {t('fuel.period.export.pdf')}
                </Button>
                <Button
                  size="sm"
                  leftSection={<IconFileZip size={16} />}
                  loading={busy === 'zip'}
                  title={t('fuel.period.export.packageHint')}
                  onClick={() => void save('zip')}
                >
                  {t('fuel.period.export.package')}
                </Button>
              </div>
            ) : null}
          </div>
          <div className={classes.body}>
            <ul className={cx.qualityCounts} aria-label={t('fuel.period.quality.kind')}>
              {QUALITY_KINDS.map((kind) => (
                <li
                  key={kind}
                  className={`${cx.qualityCount} ${period.quality.counts[kind] > 0 ? cx.qualityCount_open : ''}`}
                >
                  <span>{t(`fuel.period.quality.kinds.${kind}`)}</span>
                  <strong>{period.quality.counts[kind]}</strong>
                </li>
              ))}
            </ul>
            {period.quality.items.length === 0 ? (
              <p className={classes.hint}>{t('fuel.period.quality.empty')}</p>
            ) : (
              <div
                className={classes.tableWrap}
                // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
                tabIndex={0}
              >
                <table className={classes.table} aria-label={t('fuel.period.quality.title')}>
                  <thead>
                    <tr>
                      <th scope="col">{t('fuel.period.quality.kind')}</th>
                      <th scope="col">{t('fuel.period.quality.finding')}</th>
                      <th scope="col">{t('fuel.period.quality.record')}</th>
                      <th scope="col">{t('fuel.period.quality.at')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {period.quality.items.map((item) => (
                      <tr
                        key={`${item.code}-${item.ref.type}-${item.ref.id ?? ''}-${item.at ?? ''}-${item.detail ?? ''}`}
                      >
                        <td>{t(`fuel.period.quality.kinds.${item.kind}`)}</td>
                        <td className={cx.wrapCell}>
                          {t(`fuel.period.quality.codes.${item.code}`, {
                            until: utc(item.until),
                            defaultValue: item.code,
                          })}
                        </td>
                        <td>
                          {item.ref.type === 'tank' && item.ref.id
                            ? t('fuel.period.quality.refs.tank', { name: tankName(item.ref.id) })
                            : (item.ref.no ?? '—')}
                        </td>
                        <td>{utc(item.at) || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      ) : null}

      <LockModal
        mode={modal}
        month={month}
        source={source}
        vesselId={vesselId}
        fuelName={fuelName}
        onClose={() => setModal(null)}
        onDone={async (message) => {
          setModal(null);
          setDone(message);
          await load();
        }}
      />
    </>
  );
}

/** Kilit ve yeniden açma penceresi (ofis): yeniden açmada gerekçe ve etki raporu zorunlu. */
function LockModal({
  mode: requested,
  month,
  source,
  vesselId,
  fuelName,
  onClose,
  onDone,
}: {
  mode: 'lock' | 'reopen' | null;
  month: string;
  source: FuelSource;
  vesselId: string | null;
  fuelName: (id: string) => string;
  onClose: () => void;
  onDone: (message: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = useLedgerError();
  const reauth = useReauth('fuel_period_lock');
  const [reason, setReason] = useState('');
  const [impact, setImpact] = useState<{ report: FuelPeriodImpact; hash: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [last, setLast] = useState(requested);
  useEffect(() => {
    if (requested) setLast(requested);
  }, [requested]);
  const mode = requested ?? last;
  const editor = source.officeEditor;

  const errorRef = useRef(errorText);
  errorRef.current = errorText;
  useEffect(() => {
    if (requested !== 'reopen' || !editor?.periodImpact) return;
    let alive = true;
    editor
      .periodImpact(vesselId, month)
      .then((value) => {
        if (alive) setImpact(value);
      })
      .catch((failure) => {
        if (alive) setError(errorRef.current(failure));
      });
    return () => {
      alive = false;
    };
  }, [requested, editor, vesselId, month]);

  const close = () => {
    setReason('');
    setError(null);
    setImpact(null);
    reauth.reset();
    onClose();
  };
  const balances = (byFuel: Record<string, string>) =>
    Object.entries(byFuel)
      .map(([fuel, value]) => `${fuelName(fuel)} ${mt(value, 3)}`)
      .join(', ') || '0';
  const i = (key: string, options?: Record<string, unknown>) =>
    String(t(`fuel.period.lock.impact.${key}`, (options ?? {}) as never));
  const report = impact?.report;

  return (
    <Modal
      opened={requested !== null}
      onClose={close}
      centered
      size="lg"
      closeButtonProps={{ 'aria-label': t('fuel.period.lock.cancel') }}
      title={
        mode === 'reopen'
          ? t('fuel.period.lock.reopenTitle', { month })
          : t('fuel.period.lock.lockTitle', { month })
      }
    >
      <p style={{ marginTop: 0 }}>
        {mode === 'reopen' ? t('fuel.period.lock.reopenBody') : t('fuel.period.lock.lockBody')}
      </p>
      {mode === 'reopen' ? (
        <section className={cx.impact} aria-label={i('title')}>
          <h3 className={cx.periodLockTitle}>{i('title')}</h3>
          {report ? (
            <dl className={cx.impactList}>
              <dt>{i('records')}</dt>
              <dd>
                {i('recordsValue', report.snapshot)} → {i('recordsValue', report.current)}
              </dd>
              <dt>{i('late')}</dt>
              <dd>{i('lateValue', report.late)}</dd>
              <dt>{i('balances')}</dt>
              <dd>
                {balances(report.snapshot.byFuel)} → {balances(report.current.byFuel)} MT
              </dd>
              <dt>{i('laterMonths')}</dt>
              <dd>
                {report.laterMonths.length === 0
                  ? i('none')
                  : report.laterMonths
                      .map((m) =>
                        i('closedLocked', {
                          month: m.month,
                          locked: m.locked ? i('lockedSuffix') : '',
                        }),
                      )
                      .join(', ')}
              </dd>
              <dt>{i('voyages')}</dt>
              <dd>
                {report.voyages.length === 0
                  ? i('none')
                  : report.voyages.map((v) => v.voyageNo ?? v.id).join(', ')}
              </dd>
              <dt>{i('reports')}</dt>
              <dd>{report.consumptionReports}</dd>
              <dt>{i('quality')}</dt>
              <dd>
                {Object.entries(report.quality)
                  .filter(([, n]) => n > 0)
                  .map(([kind, n]) => `${t(`fuel.period.quality.kinds.${kind}`)}: ${n}`)
                  .join(', ') || i('none')}
              </dd>
            </dl>
          ) : (
            <p className={classes.hint}>{i('loading')}</p>
          )}
          <p className={classes.hint}>{i('mrv')}</p>
        </section>
      ) : null}
      <Textarea
        label={
          mode === 'reopen'
            ? t('fuel.period.lock.reasonRequired')
            : t('fuel.period.lock.reasonOptional')
        }
        withAsterisk={mode === 'reopen'}
        autosize
        minRows={3}
        maxLength={2000}
        value={reason}
        onChange={(event) => setReason(event.currentTarget.value)}
        mb="sm"
      />
      <ReauthField reauth={reauth} label={t('fuel.period.lock.passwordHint')} />
      {error ? (
        <Alert color="red" role="alert" mt="sm">
          {error}
        </Alert>
      ) : null}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
        <Button variant="default" onClick={close} disabled={busy}>
          {t('fuel.period.lock.cancel')}
        </Button>
        <Button
          loading={busy}
          disabled={
            reauth.missing || (mode === 'reopen' && (reason.trim() === '' || impact === null))
          }
          onClick={async () => {
            if (!editor) return;
            setBusy(true);
            setError(null);
            try {
              const body = reauth.body();
              if (mode === 'reopen' && impact && editor.reopenPeriod) {
                await editor.reopenPeriod(vesselId, month, {
                  reason: reason.trim(),
                  impactHash: impact.hash,
                  ...body,
                });
                close();
                await onDone(t('fuel.period.lock.reopenedDone', { month }));
              } else if (mode === 'lock' && editor.lockPeriod) {
                await editor.lockPeriod(vesselId, month, {
                  reason: reason.trim() || null,
                  ...body,
                });
                close();
                await onDone(t('fuel.period.lock.lockedDone', { month }));
              }
            } catch (failure) {
              reauth.noteError(failure);
              setError(errorText(failure));
            } finally {
              setBusy(false);
            }
          }}
        >
          {mode === 'reopen'
            ? t('fuel.period.lock.confirmReopen')
            : t('fuel.period.lock.confirmLock')}
        </Button>
      </div>
    </Modal>
  );
}
